import React from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { noop, theme, VStack } from '@sweetgreen/garnish';

import {
  CreditCardAddModal,
  LoadingAnimation,
  TransparentModal,
} from '@order/components';

import { useRefetchCustomerAfterPaymentMethodCreation } from '../hooks';
import { Footer, UsageHint } from '../ScanToPay.components';
import { useScanToPayNavigation } from '../ScanToPay.navigation';
import { ScanToPayQrCode } from '../ScanToPay.qrcode';
import { CreditSelector } from '../Selectors/ScanToPay.credit';
import { GiftCodeSelector } from '../Selectors/ScanToPay.giftCode';
import { PaymentMethodSelector } from '../Selectors/ScanToPay.payments';

export const ScanToPayOptions = () => {
  const { goToPayments, goToGiftCode, goBack } = useScanToPayNavigation();

  const { isLoadingQrCode, qrCode, hasBillingAccounts, onComplete } =
    useRefetchCustomerAfterPaymentMethodCreation();

  if (isLoadingQrCode) {
    return (
      <TransparentModal size="full" onClose={noop} mobileEdges={['bottom']}>
        <LoadingAnimation />
      </TransparentModal>
    );
  }

  // Customers won't have a QRCode if they have no payment method.
  const shouldShowAddCreditCardModal = !qrCode || !hasBillingAccounts;

  return (
    <TransparentModal size="full" onClose={noop} mobileEdges={['bottom']}>
      <View testID="scan-to-pay.options.route" style={styles.container}>
        {qrCode && hasBillingAccounts ? (
          <View style={styles.qrCodeContainer}>
            <ScanToPayQrCode value={qrCode} />
          </View>
        ) : (
          <LoadingAnimation />
        )}

        <View style={styles.contentContainer}>
          <ScrollView contentContainerStyle={styles.scrollContainer}>
            <VStack
              customSpacing={0}
              style={styles.stackContainer}
              divider={true}
              dividerColor={theme.colors.LIGHT}
            >
              <UsageHint />
              <PaymentMethodSelector onSelect={goToPayments} />
              <CreditSelector />
              <GiftCodeSelector onSelect={goToGiftCode} />
            </VStack>
          </ScrollView>
        </View>

        <View style={styles.footerContainer}>
          <Footer />
        </View>
      </View>

      <CreditCardAddModal
        visible={shouldShowAddCreditCardModal}
        onComplete={onComplete}
        onRequestClose={goBack}
      />
    </TransparentModal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.KHAKI_3,

    // Because native platforms use varying spacing for the screen header area
    // (even when it is disabled), we must apply different top padding per platform.
    paddingTop: Platform.select({
      ios: 0,
      web: theme.spacing['10'],
      default: theme.spacing['4'],
    }),
  },
  qrCodeContainer: {
    paddingVertical: theme.spacing['4'],
  },
  contentContainer: {
    flex: 1,
    paddingTop: theme.spacing['4'],
  },
  footerContainer: {
    paddingTop: theme.spacing['4'],
  },
  scrollContainer: {
    flexGrow: 1,
  },
  stackContainer: {
    paddingHorizontal: theme.spacing['2'],
  },
});
